import type { PickemTweetContext } from './share-pickem.types';

/**
 * Extract just the filename portion of an S3 key, minus its extension, for
 * use in the short URL. The API auto-appends `.png` server-side when the
 * imgKey has no extension, so dropping it here saves 4 chars in the tweet.
 *
 * "assets/images/pickem-share/{eventId}/1778848138594-dr8ecwrp.png" →
 * "1778848138594-dr8ecwrp"
 */
export function shortCodeFromImageKey(imageKey: string): string {
  const last = imageKey.split('/').pop() ?? imageKey;
  return last.replace(/\.[a-zA-Z0-9]+$/, '');
}

export function buildPickemShareUrl(params: {
  apiBaseUrl: string;
  eventId: string;
  /** Short code derived from the uploaded image key (filename only). */
  imageShortCode?: string;
}): string {
  const base = params.apiBaseUrl.replace(/\/$/, '');
  if (params.imageShortCode) {
    return `${base}/api/v1/pickem/s/${params.eventId}/${params.imageShortCode}`;
  }
  // Fallback when sharing without an image — old long-form endpoint still
  // works as a no-image landing page.
  return `${base}/api/v1/pickem/events/${params.eventId}/share`;
}

/**
 * Convert a free-form event name into a Twitter-friendly hashtag.
 * "Mainstage Madness" → "#MainstageMadness"
 */
export function eventHashtag(eventName: string): string {
  const cleaned = eventName
    .normalize('NFKD')
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .trim();
  if (!cleaned) return '#2XKO';
  const pascal = cleaned
    .split(/\s+/)
    .map((word) =>
      word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : '',
    )
    .join('');
  return `#${pascal}`;
}

export function buildPickemTweetText(ctx: PickemTweetContext): string {
  const tag = ctx.hashtag?.trim() || eventHashtag(ctx.eventName);
  const hasFilledBracket =
    (ctx.picksMade ?? 0) > 0 &&
    ctx.totalPicks != null &&
    ctx.picksMade === ctx.totalPicks;
  const hasAnyPicks = (ctx.picksMade ?? 0) > 0;

  // NOTE: we deliberately don't write "mainstage.win" as a bare domain in the
  // text body. Twitter auto-linkifies bare domains and then prefers them for
  // unfurling, which would steal the card preview away from our share URL.
  const opener = hasFilledBracket
    ? `I just filled out my fantasy bracket for ${ctx.eventName} on MainStage`
    : hasAnyPicks
      ? `I'm picking ${ctx.picksMade}/${ctx.totalPicks} for ${ctx.eventName} on MainStage`
      : `Make your picks for ${ctx.eventName} on MainStage`;

  return `${opener}\n\n${tag}`;
}

export function buildPickemTweetUrl(text: string, url?: string): string {
  // Per https://docs.x.com/x-for-websites/post-button/overview the canonical
  // intent endpoint is x.com (not twitter.com). The post composer auto-shortens
  // the URL when present.
  const params = new URLSearchParams();
  params.set('text', text);
  if (url) params.set('url', url);
  return `https://x.com/intent/tweet?${params.toString()}`;
}
